import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, FolderPlus, X, Mail, Copy, Link2, Download, Clock,
  FileText, Eye, EyeOff, CheckCircle2, ArrowUpFromLine, History, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_ACCENT = "var(--xl-primary)";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function stripExtension(name) {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

function titleFromFiles(fileList) {
  if (!fileList?.length) return "";
  if (fileList.length === 1) return stripExtension(fileList[0].name);
  const base = stripExtension(fileList[0].name);
  return `${base} + ${fileList.length - 1} more`;
}

export default function Sharing() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [shareTab, setShareTab] = useState("link");
  const [files, setFiles] = useState([]);
  const [recipientEmails, setRecipientEmails] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [title, setTitle] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [expiryDays, setExpiryDays] = useState("7");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastLink, setLastLink] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const autoTitleRef = useRef("");

  useEffect(() => {
    if (user?.email && !senderEmail) {
      setSenderEmail(user.email);
    }
  }, [user?.email, senderEmail]);

  const fetchTransfers = useCallback(async () => {
    if (!isAuthenticated) {
      setTransfers([]);
      setLoadingTransfers(false);
      return;
    }
    setLoadingTransfers(true);
    try {
      const res = await axios.get(`${API}/transfers`);
      setTransfers(res.data.transfers ?? []);
    } catch (e) {
      console.error(e);
    }
    setLoadingTransfers(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading) return;
    fetchTransfers();
  }, [fetchTransfers, isLoading]);

  const syncTitleFromFiles = (fileList) => {
    const nextTitle = titleFromFiles(fileList);
    setTitle((current) => (
      current === "" || current === autoTitleRef.current ? nextTitle : current
    ));
    autoTitleRef.current = nextTitle;
  };

  const addFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);
    setFiles((prev) => {
      const next = [...prev, ...fileArray];
      syncTitleFromFiles(next);
      return next;
    });
    setLastLink(null);
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      syncTitleFromFiles(next);
      return next;
    });
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleTransfer = async () => {
    if (files.length === 0) { toast.error("Add at least one file"); return; }
    if (shareTab === "email" && !recipientEmails.trim()) {
      toast.error("Enter at least one recipient email");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));
      formData.append("title", title || "Untitled Transfer");
      formData.append("message", message);
      formData.append("sender_email", senderEmail);
      formData.append("recipient_emails", shareTab === "email" ? recipientEmails : "");
      formData.append("expires_in_days", expiryDays);
      if (password) formData.append("password", password);

      const res = await axios.post(`${API}/transfers`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const shareUrl = `${window.location.origin}/shared/${res.data.token}`;
      setLastLink(shareUrl);
      toast.success(shareTab === "email" ? "Transfer sent successfully!" : "Link created successfully!");
      const notif = res.data.email_notifications;
      if (notif) {
        if (notif.skipped) {
          toast.warning("Email notifications skipped — RESEND_API_KEY not configured");
        } else if (notif.sent > 0) {
          toast.success(
            `Notified ${notif.sent} of ${notif.total} email${notif.total === 1 ? "" : "s"}` +
            (notif.failed > 0 ? ` (${notif.failed} failed)` : "")
          );
        } else if (notif.total === 0 && shareTab === "link") {
          toast.info("Link ready — copy it below");
        } else if (notif.failed > 0) {
          toast.error(`Failed to send ${notif.failed} notification(s) — link is still active`);
        }
      }
      setFiles([]);
      setTitle("");
      autoTitleRef.current = "";
      setMessage("");
      setPassword("");
      setRecipientEmails("");
      if (!user?.email) setSenderEmail("");
      fetchTransfers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const toggleTransfer = async (id) => {
    try {
      await axios.put(`${API}/transfers/${id}/toggle`);
      fetchTransfers();
      toast.success("Transfer updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const renderShareForm = () => (
    <div className="space-y-4 h-full flex flex-col">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="recipient-email" className="text-xs text-muted-foreground font-normal">
            Recipient email{shareTab === "link" ? " (optional)" : ""}
          </Label>
          <Input
            id="recipient-email"
            data-testid="recipient-email"
            placeholder="email@example.com"
            value={recipientEmails}
            onChange={(e) => setRecipientEmails(e.target.value)}
            className="h-10 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sender-email" className="text-xs text-muted-foreground font-normal">
            Your email (optional)
          </Label>
          <Input
            id="sender-email"
            data-testid="sender-email"
            placeholder="you@example.com"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            className="h-10 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="transfer-title" className="text-xs text-muted-foreground font-normal">
            Title
          </Label>
          <Input
            id="transfer-title"
            data-testid="transfer-title"
            placeholder="e.g. CT Scan Results"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-10 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="transfer-password" className="text-xs text-muted-foreground font-normal">
            Password (optional)
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              id="transfer-password"
              data-testid="transfer-password"
              type={showPassword ? "text" : "password"}
              placeholder="Protect with password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 text-sm pl-9 pr-9"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="transfer-message" className="text-xs text-muted-foreground font-normal">
          Message (optional)
        </Label>
        <Textarea
          id="transfer-message"
          data-testid="transfer-message"
          placeholder="Add a note for the recipient..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="text-sm resize-none min-h-[80px]"
        />
      </div>

      <div className="space-y-3 pt-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Link expires in</span>
          <Select value={expiryDays} onValueChange={setExpiryDays}>
            <SelectTrigger data-testid="expiry-select" className="w-[120px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 day</SelectItem>
              <SelectItem value="3">3 days</SelectItem>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {uploading ? (
          <div className="space-y-2">
            <div
              className="h-11 rounded-lg flex items-center justify-center text-white text-sm font-medium"
              style={{ background: SHARE_ACCENT }}
            >
              Uploading... {uploadProgress}%
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%`, background: SHARE_ACCENT }}
              />
            </div>
          </div>
        ) : lastLink ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[#0D9488]/8 border border-[#0D9488]/20">
              <CheckCircle2 className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
              <span className="text-xs text-[#0D9488] font-medium truncate flex-1">
                {shareTab === "email" ? "Transfer sent!" : "Link created!"}
              </span>
            </div>
            <button
              data-testid="copy-last-link"
              onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Link copied!"); }}
              className="w-full h-11 rounded-lg bg-[var(--xl-text)] hover:bg-[#0F1A2E] text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Link
            </button>
          </div>
        ) : (
          <Button
            data-testid="transfer-btn"
            onClick={handleTransfer}
            disabled={files.length === 0}
            className="w-full h-11 rounded-lg text-white text-sm font-medium gap-2 hover:opacity-90"
            style={{ background: SHARE_ACCENT }}
          >
            {shareTab === "email" ? (
              <>
                <Mail className="w-4 h-4" /> Send Email
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" /> Create Link
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="sharing-page">
      {/* New Transfer */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpFromLine className="w-4 h-4 text-[var(--xl-primary)]" />
              <h2 className="text-sm font-semibold">
                New Transfer
              </h2>
            </div>
            <p className="text-xs text-muted-foreground">Share medical files securely</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch">
            {/* Left: file drop zone */}
            <div className="p-5 lg:border-r border-border flex flex-col">
              <div
                className={`rounded-xl border-2 border-dashed transition-colors flex-1 h-full min-h-[320px] lg:min-h-0 flex flex-col ${
                  dragOver ? "border-[var(--xl-primary)] bg-[var(--xl-primary-soft)]" : "border-[var(--xl-line2)] bg-[var(--xl-bg)]"
                } ${files.length > 0 ? "p-4" : "p-6 justify-center"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="file-drop-zone"
              >
                {files.length === 0 ? (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Drag & drop files here</p>
                    <p className="text-xs text-muted-foreground mb-5">or choose from your device</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button
                        type="button"
                        data-testid="add-files-btn"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-white hover:bg-muted/50 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" /> Add files
                      </button>
                      <button
                        type="button"
                        data-testid="add-folders-btn"
                        onClick={() => folderInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-white hover:bg-muted/50 transition-colors text-sm font-medium"
                      >
                        <FolderPlus className="w-4 h-4" /> Add folder
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">
                        {files.length} file{files.length > 1 ? "s" : ""} ({formatBytes(totalSize)})
                      </span>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs text-[var(--xl-primary)] hover:underline font-medium"
                      >
                        + Add more
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-1">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-md bg-white border border-border/60 group">
                          <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate flex-1">{f.name}</span>
                          <span className="text-muted-foreground flex-shrink-0">{formatBytes(f.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-destructive/10 rounded transition-opacity"
                            data-testid={`remove-file-${i}`}
                          >
                            <X className="w-3 h-3 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="file-input"
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                className="hidden"
                {...{ webkitdirectory: "", directory: "" }}
                onChange={(e) => addFiles(e.target.files)}
                data-testid="folder-input"
              />
            </div>

            {/* Right: share form with tabs */}
            <div className="p-5 flex flex-col">
              <Tabs value={shareTab} onValueChange={setShareTab} className="w-full flex flex-col flex-1">
                <TabsList className="w-full grid grid-cols-2 h-10 mb-4 shrink-0">
                  <TabsTrigger value="email" className="gap-1.5 text-sm" data-testid="tab-send-email">
                    <Mail className="w-3.5 h-3.5" /> Send email
                  </TabsTrigger>
                  <TabsTrigger value="link" className="gap-1.5 text-sm" data-testid="tab-create-link">
                    <Link2 className="w-3.5 h-3.5" /> Create link
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="email" className="mt-0 flex-1">
                  {renderShareForm()}
                </TabsContent>
                <TabsContent value="link" className="mt-0 flex-1">
                  {renderShareForm()}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer History — signed-in users only */}
      {isAuthenticated && (
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2 mb-1">
              <History className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">
                Transfer History
              </h2>
            </div>
            <p className="text-xs text-muted-foreground">
              {transfers.length} transfer{transfers.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div>
            {loadingTransfers ? (
              <div className="p-10 text-center text-sm text-muted-foreground">Loading...</div>
            ) : transfers.length === 0 ? (
              <div className="p-10 text-center">
                <Link2 className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No transfers yet</p>
                <p className="text-xs text-muted-foreground mt-1">Upload files above to share securely</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {transfers.map((t, i) => (
                  <div
                    key={t.id}
                    className="px-5 py-3.5 hover:bg-muted/30 transition-colors"
                    data-testid={`transfer-row-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium truncate">{t.title}</span>
                          <Badge
                            variant={t.is_active ? "default" : "secondary"}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {t.is_active ? "Active" : "Disabled"}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>{t.file_count} file{t.file_count !== 1 ? "s" : ""}</span>
                          <span>{formatBytes(t.total_size)}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t.expires_in_days}d
                          </span>
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {t.download_count}
                          </span>
                          <span>{timeAgo(t.created_at)}</span>
                        </div>
                        {t.recipient_emails?.length > 0 && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            To: {t.recipient_emails.join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          data-testid={`copy-transfer-${i}`}
                          onClick={() => copyLink(t.token)}
                          className="p-1.5 rounded-md hover:bg-secondary transition-colors"
                          title="Copy link"
                        >
                          <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          data-testid={`toggle-transfer-${i}`}
                          onClick={() => toggleTransfer(t.id)}
                          className="p-1.5 rounded-md hover:bg-secondary transition-colors"
                          title={t.is_active ? "Disable" : "Enable"}
                        >
                          {t.is_active ? (
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
